import React, { useState, useEffect } from 'react';
import {
  Box, Tabs, Tab, Grid, Paper, Dialog, DialogTitle, IconButton,
  Typography, Alert, useTheme, useMediaQuery, Drawer, Button, AppBar, Toolbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import MenuIcon from '@mui/icons-material/Menu';
import AppointmentList from './components/AppointmentList';
import AppointmentDetail from './components/AppointmentDetail';
import AppointmentForm from './components/AppointmentForm';
import {
  getAppointments, createAppointment, updateAppointment, deleteAppointment,
} from '../../api/appointment.api';
import { getPatients } from '../../api/patient.api';

function AppointmentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [tab, setTab] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsRes, patientsRes] = await Promise.all([
        getAppointments(),
        getPatients(),
      ]);
      setAppointments(appointmentsRes.appointments || []);
      setPatients(patientsRes.patients || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAppointment = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);

  const handleSaveAppointment = async (appointmentData) => {
    try {
      if (appointmentData._id) {
        await updateAppointment(appointmentData._id, appointmentData);
      } else {
        await createAppointment(appointmentData);
      }
      setAddDialogOpen(false);
      setTab(0);
      fetchData();
    } catch (err) {
      console.error('Error saving appointment:', err);
      setError('Failed to save appointment. Please try again.');
    }
  };

  const handleAppointmentDelete = async (appointment) => {
    if (!window.confirm(`Delete appointment for ${appointment.patientName}?`)) {
      return;
    }
    try {
      await deleteAppointment(appointment._id);
      fetchData();
      if (selectedAppointment?._id === appointment._id) {
        setSelectedAppointment(null);
      }
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment. Please try again.');
    }
  };

  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // Mobile layout
  if (isMobile) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Appointments
            </Typography>
            <Button
              color="inherit"
              startIcon={<EventIcon />}
              onClick={handleAddAppointment}
            >
              Add
            </Button>
          </Toolbar>
        </AppBar>

        {error && (
          <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1, p: 1 }}>
          <Paper elevation={2} sx={{ height: '100%', p: 2 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
              <Tab label="Details" />
              <Tab label="Edit" />
            </Tabs>
            <Box sx={{ mt: 2, overflow: 'auto', height: 'calc(100% - 60px)' }}>
              {tab === 0 && (
                <AppointmentDetail
                  appointment={selectedAppointment}
                  onEdit={(appointment) => {
                    setSelectedAppointment(appointment);
                    setTab(1);
                  }}
                />
              )}
              {tab === 1 && (
                <AppointmentForm
                  appointment={selectedAppointment}
                  patients={patients}
                  onSave={handleSaveAppointment}
                  onCancel={() => setTab(0)}
                  mode="edit"
                />
              )}
            </Box>
          </Paper>
        </Box>

        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: isSmallMobile ? '100%' : 320 } }}
        >
          <AppointmentList
            appointments={appointments}
            loading={loading}
            onAppointmentSelect={handleAppointmentSelect}
            onAppointmentEdit={(appointment) => {
              setSelectedAppointment(appointment);
              setTab(1);
              setMobileDrawerOpen(false);
            }}
            onAppointmentDelete={handleAppointmentDelete}
            onAddAppointment={handleAddAppointment}
            selectedAppointment={selectedAppointment}
          />
        </Drawer>

        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
          >
            <EventIcon />
            <Typography variant="h6" sx={{ flex: 1 }}>Add New Appointment</Typography>
            <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <AppointmentForm
              patients={patients}
              onSave={handleSaveAppointment}
              onCancel={handleCloseAddDialog}
              mode="add"
            />
          </Box>
        </Dialog>
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 3 }, height: '100vh', display: 'flex', flexDirection: 'column',
    }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Appointment List */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <AppointmentList
              appointments={appointments}
              loading={loading}
              onAppointmentSelect={handleAppointmentSelect}
              onAppointmentEdit={(appointment) => {
                setSelectedAppointment(appointment);
                setTab(1);
              }}
              onAppointmentDelete={handleAppointmentDelete}
              onAddAppointment={handleAddAppointment}
              selectedAppointment={selectedAppointment}
            />
          </Paper>
        </Grid>

        {/* Appointment Details/Edit */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              height: '100%', p: 2, display: 'flex', flexDirection: 'column',
            }}
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label="Details" />
              <Tab label="Edit" />
            </Tabs>
            <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
              {tab === 0 && (
                <AppointmentDetail
                  appointment={selectedAppointment}
                  onEdit={(appointment) => {
                    setSelectedAppointment(appointment);
                    setTab(1);
                  }}
                />
              )}
              {tab === 1 && (
                <AppointmentForm
                  appointment={selectedAppointment}
                  patients={patients}
                  onSave={handleSaveAppointment}
                  onCancel={() => setTab(0)}
                  mode="edit"
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Appointment Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          px: 3,
        }}
        >
          <EventIcon sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Add New Appointment
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <AppointmentForm
            patients={patients}
            onSave={handleSaveAppointment}
            onCancel={handleCloseAddDialog}
            mode="add"
          />
        </Box>
      </Dialog>
    </Box>
  );
}

export default AppointmentsPage;
